import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { SubdivisionDto } from '@eombor/contracts';
import { Button, Dialog, Input } from '@eombor/ui';
import { zodResolver } from '../../../shared/form/zod-resolver.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';

/**
 * Yaratishda KOD va EGASI kerak, tahrirlashda esa ikkalasi ham qulflangan.
 *
 * Sababi backendda ham xuddi shu (`updateSubdivisionSchema` faqat nom va
 * faollikni oladi): bo'linmaga xodimlar, talabnomalar va qoldiq bog'langan
 * bo'ladi. Egasi almashsa, o'sha qoldiq bir kechada boshqa korxonaga o'tib
 * ketardi. Kerak bo'lsa — eskisi faolsizlantiriladi va yangisi ochiladi.
 */
export const subdivisionFormSchema = z.object({
  code: z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/, t('subdivision.errorCode')),
  name: z.string().min(2, t('subdivision.errorName')).max(200),
  orgUnitId: z.string().min(1, t('subdivision.errorOrgUnit')),
  isActive: z.boolean(),
});

export type SubdivisionFormValues = z.infer<typeof subdivisionFormSchema>;

export interface SubdivisionOwnerOption {
  id: string;
  name: string;
  /**
   * Rasmiy belgi — «MTU-5», «PCH-70».
   *
   * Ro'yxatda NOM YOLG'IZ turmaydi va bu shart: tuzilmada bir xil nomli
   * ikki yozuv bo'ladi (MTU ning o'zi va uning apparati). Faqat nom
   * ko'rsatilganda ular ekranda AYNAN bir xil ikki qator bo'lib chiqardi va
   * foydalanuvchi bo'limni qaysi biriga biriktirganini bilmasdi — keyin esa
   * xodimga o'sha bo'lim ko'rinmay qolardi.
   */
  code: string;
  mtuId: string | null;
}

export interface SubdivisionDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` — yangi bo'linma; aks holda tahrirlash. */
  subdivision: SubdivisionDto | null;
  /** Egasi bo'la oladigan korxona va MTU lar — doira bilan cheklangan. */
  owners: readonly SubdivisionOwnerOption[];
  /**
   * Ro'yxat hali yuklanmoqdami.
   *
   * Ajratib ko'rsatiladi, chunki "yuklanmoqda" bilan "bitta ham korxona
   * yo'q" ikki xil holat va ikkalasi ham bo'sh tanlagich ko'rinishida
   * chiqsa, foydalanuvchi kutishi kerakmi yoki nimadir buzilganmi —
   * bilmaydi.
   */
  ownersPending?: boolean;
  onSubmit: (values: SubdivisionFormValues) => void;
  pending?: boolean;
  error?: string | null;
}

export const SubdivisionDialog = ({
  open,
  onClose,
  subdivision,
  owners,
  ownersPending = false,
  onSubmit,
  pending = false,
  error = null,
}: SubdivisionDialogProps) => {
  const editing = subdivision !== null;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SubdivisionFormValues>({
    resolver: zodResolver(subdivisionFormSchema),
    defaultValues: { code: '', name: '', orgUnitId: '', isActive: true },
  });

  useEffect(() => {
    if (!open) return;
    reset(
      subdivision === null
        ? { code: '', name: '', orgUnitId: '', isActive: true }
        : {
            code: subdivision.code,
            name: subdivision.name,
            orgUnitId: subdivision.orgUnitId,
            isActive: subdivision.isActive,
          },
    );
  }, [open, subdivision, reset]);

  const orgUnitId = watch('orgUnitId');
  const submit = handleSubmit((values) => onSubmit(values));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('subdivision.dialogEditTitle') : t('subdivision.dialogCreateTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('subdivision.cancel')}
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            {t('subdivision.save')}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {error !== null ? (
          <p
            role="alert"
            className="rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        ) : null}

        {/* Tahrirlashda egasi MATN bo'lib turadi: uni o'zgartirib bo'lmaydi,
            lekin qaysi korxonaniki ekani ko'rinib turishi kerak. */}
        {editing ? (
          <p className="text-sm text-text-muted">
            {t('subdivision.fieldOrgUnit')}: {subdivision.orgUnitName}
          </p>
        ) : (
          <Select
            label={t('subdivision.fieldOrgUnit')}
            placeholder={
              ownersPending ? t('subdivision.ownersLoading') : t('subdivision.ownerPlaceholder')
            }
            options={owners.map((owner) => ({
              value: owner.id,
              label: `${owner.name} · ${owner.code}`,
            }))}
            value={orgUnitId}
            disabled={ownersPending}
            hint={
              !ownersPending && owners.length === 0 ? t('subdivision.ownersEmpty') : undefined
            }
            onChange={(event) => setValue('orgUnitId', event.target.value)}
            error={errors.orgUnitId?.message}
          />
        )}

        <Input
          label={t('subdivision.fieldCode')}
          hint={editing ? undefined : t('subdivision.codeHint')}
          error={errors.code?.message}
          disabled={editing}
          {...register('code')}
        />

        <Input label={t('subdivision.fieldName')} error={errors.name?.message} {...register('name')} />

        <label className="flex cursor-pointer items-center gap-2 text-sm text-text">
          <input type="checkbox" className="size-4 accent-accent" {...register('isActive')} />
          {t('subdivision.statusActive')}
        </label>

        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden>
          {t('subdivision.save')}
        </button>
      </form>
    </Dialog>
  );
};
